import React, { useEffect, useState, lazy, Suspense } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { ArrowLeft, Bell, BellOff, ExternalLink, Tag, Pin } from "lucide-react";
import Header from "../../app/nav/Header";
import Login from "../../app/auth/Login";
import { getToken } from "../../app/auth/authConfig";
import axios from "axios";
const FileViewModal = lazy(() => import("../../components/modal/FileViewModal"));
const ReactMarkdown = lazy(() => import("react-markdown"));
const ProductDocumentActions = lazy(() => import("../../components/reuseable/ProductDocumentActions"));

const Label = ({ icon: Icon, text }) => (
  <span className="flex items-center gap-1 text-[#7d1fa3]/80 text-sm font-semibold mb-1">
    <Icon className="w-4 h-4 text-[#E12AFB]" /> {text}
  </span>
);

const FieldSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-4 w-28 bg-[#7d1fa3]/15 rounded mb-2" />
    <div className="h-5 w-full max-w-xl bg-[#7d1fa3]/10 rounded" />
  </div>
);

const ToolDetailsSkeleton = () => (
  <div className="mt-4 z-10 relative flex flex-col gap-6 animate-pulse">
    <FieldSkeleton />
    <FieldSkeleton />
    <div>
      <div className="h-4 w-32 bg-[#7d1fa3]/15 rounded mb-2" />
      <div className="h-20 w-full bg-[#7d1fa3]/10 rounded-xl" />
    </div>
    <FieldSkeleton />
    <FieldSkeleton />
    <div>
      <div className="h-4 w-20 bg-[#7d1fa3]/15 rounded mb-2" />
      <div className="h-56 w-full bg-[#7d1fa3]/10 rounded-xl" />
      <div className="h-4 w-40 bg-[#7d1fa3]/12 rounded mt-3" />
    </div>
    <FieldSkeleton />
  </div>
);

const MarkdownFallback = () => (
  <div className="space-y-2 animate-pulse">
    <div className="h-4 w-full bg-[#7d1fa3]/10 rounded" />
    <div className="h-4 w-11/12 bg-[#7d1fa3]/10 rounded" />
    <div className="h-4 w-4/5 bg-[#7d1fa3]/10 rounded" />
  </div>
);

const ActionsFallback = () => (
  <div className="flex gap-2 mt-2 sm:mt-0 animate-pulse">
    <div className="h-10 w-28 rounded-xl bg-[#7d1fa3]/10" />
    <div className="h-10 w-48 rounded-xl bg-[#7d1fa3]/10" />
    <div className="h-10 w-44 rounded-xl bg-[#7d1fa3]/10" />
  </div>
);

const parseFileList = (value) => {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const getDisplayFileName = (filePath) => {
  if (!filePath) return "";
  const parts = String(filePath).split("_");
  return parts.length > 2 ? parts.slice(2).join("_") : filePath;
};

const ToolDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showFileViewModal, setShowFileViewModal] = useState(false);
  const [selectedProductFile, setSelectedProductFile] = useState("");
  const [selectedPreviewFile, setSelectedPreviewFile] = useState("");
  const [selectedPreviewName, setSelectedPreviewName] = useState("");
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [subscribeLoading, setSubscribeLoading] = useState(false);
    const [subscribeMsg, setSubscribeMsg] = useState("");

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setIsLoading(true);
        setError("");
        const token = await getToken();
        const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/tools/${id}`;
        const res = await axios.get(baseURL, {
          headers: { "oidc-token": token },
        });
        setDetails(res.data);
      } catch (err) {
        console.error("Tool details fetch error", err);
        setError("Failed to load tool details");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchDetails();
    }
  }, [id]);

    useEffect(() => {
      const checkSubscription = async () => {
        try {
          const token = await getToken();
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_API_URL}/api/notifications/tools/${id}/subscription-status`,
            { headers: { "oidc-token": token } }
          );
          setIsSubscribed(res.data.subscribed);
        } catch (err) {
          console.error("Subscription status check failed", err);
        }
      };
      if (id) checkSubscription();
    }, [id]);

    const handleSubscribeToggle = async () => {
      setSubscribeLoading(true);
      setSubscribeMsg("");
      try {
        const token = await getToken();
        const base = `${process.env.REACT_APP_BACKEND_API_URL}/api/notifications/tools/${id}`;
        if (isSubscribed) {
          await axios.delete(`${base}/unsubscribe`, { headers: { "oidc-token": token } });
          setIsSubscribed(false);
          setSubscribeMsg("Unsubscribed from tool updates.");
        } else {
          await axios.post(`${base}/subscribe`, {}, { headers: { "oidc-token": token } });
          setIsSubscribed(true);
          setSubscribeMsg("Subscribed! You'll be notified when this tool is updated.");
        }
      } catch (err) {
        console.error("Subscribe toggle error", err);
        setSubscribeMsg("Something went wrong. Please try again.");
      } finally {
        setSubscribeLoading(false);
        setTimeout(() => setSubscribeMsg(""), 4000);
      }
    };

  const productFiles = parseFileList(details?.filePath);
  const onboardingFiles = parseFileList(details?.onboardingFilePath);

  const primaryOnboardingFile = onboardingFiles[0] || null;

  useEffect(() => {
    if (productFiles.length > 0) {
      setSelectedProductFile((prev) => (prev && productFiles.includes(prev) ? prev : productFiles[0]));
    } else {
      setSelectedProductFile("");
    }
  }, [details]);

  const buildDownloadUrl = (filename) =>
    `${process.env.REACT_APP_BACKEND_API_URL}/api/tools/download/${encodeURIComponent(filename)}`;

  const openPreview = (filePath) => {
    setSelectedPreviewFile(filePath);
    setSelectedPreviewName(getDisplayFileName(filePath));
    setShowFileViewModal(true);
  };

  const downloadUrl = selectedProductFile ? buildDownloadUrl(selectedProductFile) : null;
  const onboardingDownloadUrl = primaryOnboardingFile ? buildDownloadUrl(primaryOnboardingFile) : null;

  return (
    <>
      <AuthenticatedTemplate>
        <div className="w-full min-h-screen flex flex-col">
          <Header />
          <div className="w-full mx-auto px-6 py-8">
            <button
              onClick={() => navigate("/application")}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/50 border border-white/60 hover:bg-white/70 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Applications
            </button>

            <div className="mt-6 rounded-2xl bg-white backdrop-blur-md p-8  border-0 relative overflow-hidden width-full  mx-auto">
              <div className="absolute -top-10 -right-10 w-56 h-56 bg-[#E12AFB]/10 rounded-full blur-2xl z-0" />

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2 z-10 relative">
                <h1 className="text-3xl font-bold text-[#7d1fa3]">Tool Details</h1>
                {isLoading ? (
                  <ActionsFallback />
                ) : (
                  <Suspense fallback={<ActionsFallback />}>
                    <ProductDocumentActions
                      productFiles={productFiles}
                      selectedProductFile={selectedProductFile}
                      setSelectedProductFile={setSelectedProductFile}
                      downloadUrl={downloadUrl}
                      onboardingDownloadUrl={onboardingDownloadUrl}
                      onViewFile={() => selectedProductFile && openPreview(selectedProductFile)}
                      canViewFile={Boolean(selectedProductFile)}
                      isSubscribed={isSubscribed}
                      subscribeLoading={subscribeLoading || isLoading}
                      onSubscribeToggle={handleSubscribeToggle}
                    />
                  </Suspense>
                )}
              </div>
                {subscribeMsg && (
                  <div className="flex items-center gap-2 mb-2 z-10 relative">
                    <span className={`text-sm font-medium ${isSubscribed ? "text-[#7d1fa3]" : "text-red-500"}`}>
                      {subscribeMsg}
                    </span>
                  </div>
                )}
              <p className="text-[#7d1fa3]/70 text-sm z-10 relative">View metadata, open the document preview, or download the file directly.</p>

              {isLoading && <ToolDetailsSkeleton />}

              {!isLoading && error && (
                <div className="py-8 text-red-500 z-10 relative">
                  <p>{error}</p>
                </div>
              )}

              {!isLoading && !error && details && (
                <div className="mt-4 z-10 relative flex flex-col gap-6">
                  <div>
                    <Label icon={Pin} text="Name" />
                    <div className="text-[#3a3a3a] text-base font-medium mb-2">{details.toolName || "-"}</div>
                  </div>
                  <div>
                    <Label icon={Tag} text="Type" />
                    <div className="text-[#3a3a3a] text-base font-medium mb-2">{details.tooltype || "-"}</div>
                  </div>
                  <div>
                    <Label icon={Pin} text="Owner Email" />
                    <div className="text-[#3a3a3a] text-base font-medium mb-2">
                      {details.ownerEmail ? (
                        <a
                          href={`mailto:${details.ownerEmail}`}
                          className="text-blue-600 hover:text-blue-500 underline"
                        >
                          {details.ownerEmail}
                        </a>
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                  <div>
                    <Label icon={Pin} text="Description" />
                    <div className="text-[#3a3a3a] text-base break-words leading-relaxed mb-2 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_strong]:text-[#7d1fa3] [&_a]:text-blue-600 [&_a]:underline">
                      <Suspense fallback={<MarkdownFallback />}>
                        <ReactMarkdown>{details.description || ""}</ReactMarkdown>
                      </Suspense>
                    </div>
                  </div>
                  <div>
                    <Label icon={Tag} text="Tags" />
                    <div className="text-[#3a3a3a] text-base font-medium mb-2">{details.tags || "-"}</div>
                  </div>
                 
                  <div>
                    <Label icon={Pin} text="Created At" />
                    <div className="text-[#3a3a3a] text-base font-medium mb-2">{details.createdAt ? new Date(details.createdAt).toLocaleString() : "-"}</div>
                  </div>
                 
                  <div>
                    <Label icon={Pin} text="Product Documents" />
                    {productFiles.length > 0 ? (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {productFiles.map((file, index) => (
                          <button
                            key={`${file}-${index}`}
                            type="button"
                            onClick={() => openPreview(file)}
                            className="px-3 py-1.5 rounded-lg bg-[#E12AFB]/10 text-[#7d1fa3] text-sm hover:bg-[#E12AFB]/20 transition-colors"
                          >
                            {getDisplayFileName(file) || `Product File ${index + 1}`}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-[#3a3a3a] text-base font-medium mb-2">-</div>
                    )}
                  </div>
                  <div>
                    <Label icon={Pin} text="Onboarding Files" />
                    {onboardingFiles.length > 0 ? (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {onboardingFiles.map((file, index) => (
                          <button
                            key={`${file}-${index}`}
                            type="button"
                            onClick={() => openPreview(file)}
                            className="px-3 py-1.5 rounded-lg bg-[#34d399]/15 text-[#065f46] text-sm hover:bg-[#34d399]/25 transition-colors"
                          >
                            {getDisplayFileName(file) || `Onboarding File ${index + 1}`}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-[#3a3a3a] text-base font-medium mb-2">-</div>
                    )}
                  </div>

                   <div>
                    <Label icon={Tag} text="URL" />
                    <div className="text-base mb-2 flex flex-col gap-2">
                      {details.url ? (
                        <>
                          <iframe
                            src={details.url}
                            title="Tool URL Preview"
                            className="w-full rounded-xl border border-[#E12AFB]/20 min-h-[300px] mb-2"
                            style={{ minHeight: 300, background: '#fff' }}
                            sandbox="allow-scripts allow-same-origin allow-popups"
                          />
                          <a
                            href={details.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-500 inline-flex items-center gap-1 font-medium"
                          >
                            Click to view the link
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        </>
                      ) : (
                        <span className="text-[#3a3a3a]">-</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showFileViewModal && selectedPreviewFile && (
            <Suspense
              fallback={
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl p-6 shadow-xl text-[#7d1fa3] font-medium">Loading file preview...</div>
                </div>
              }
            >
              <FileViewModal
                filePath={selectedPreviewFile}
                fileName={selectedPreviewName || details.toolName}
                onClose={() => {
                  setShowFileViewModal(false);
                  setSelectedPreviewFile("");
                  setSelectedPreviewName("");
                }}
              />
            </Suspense>
          )}
        </div>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Login />
      </UnauthenticatedTemplate>
    </>
  );
};
export default ToolDetails;
